import React, { useEffect, useState, useRef, useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import GenericTableSkeleton from "../../../../skeletons/tableSkeleton/genericTableSkeleton";
import * as FeatherIcon from "react-feather";
import "./EmailTiptapEditor.css";

export default function EmailTiptapEditor({
  email,
  allowEditing,
  onUpdate,
  setEditing,
  setTemplateId,
}) {
  const TEMPLATE_ID = email?._id;
  const [subject, setSubject] = useState(email?.subject || "");
  const debounceRef = useRef(null);
  const lastSavedContent = useRef(email?.bodycontent || "");
  const skipNextUpdate = useRef(false); // 🧩 NEW FLAG

  /** ✅ Initialize TipTap editor */
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      Image,
      Placeholder.configure({
        placeholder: "Write your email content here...",
      }),
    ],
    editable: allowEditing,
    content: email?.bodycontent || "",
    onUpdate: ({ editor }) => {
      if (skipNextUpdate.current) {
        // 👇 ignore this one programmatic update
        skipNextUpdate.current = false;
        return;
      }

      const html = editor.getHTML();
      if (html !== lastSavedContent.current) {
        setEditing(true);
        handleDebouncedUpdate(html);
      }
    },
  });

  /** ✅ Handle prop changes (email switching) */
  useEffect(() => {
    if (!email) return;
    setSubject(email.subject || "");
    setTemplateId(email._id);
    setEditing(false);

    if (editor && email.bodycontent !== lastSavedContent.current) {
      skipNextUpdate.current = true; // 🚫 prevents onUpdate firing
      editor.commands.setContent(email.bodycontent || "", false);
      lastSavedContent.current = email.bodycontent || "";
    }
  }, [email?._id,email.subject,email.bodycontent]);

  /** ✅ Debounced update trigger */
const handleDebouncedUpdate = useCallback(
  (newBody, newSubject = subject) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      lastSavedContent.current = newBody;
      onUpdate?.(TEMPLATE_ID, {
        subject: newSubject.trim(),
        bodycontent: newBody,
      });

      // showBanner?.({
      //   message: "Email updated",
      //   title: "Success",
      //   type: "success",
      //   duration: 2000,
      // });

      setEditing(false);
    }, 700);
  },
  [onUpdate, TEMPLATE_ID, subject] // ✅ subject here is safe fallback
);


  /** ✅ Update subject just like body */
const handleSubjectChange = (e) => {
  const val = e.target.value;
  setSubject(val);
  setEditing(true);
  handleDebouncedUpdate(editor?.getHTML() || "", val);
};


  return (
    <div className="email-tiptap-editor-container">
      {subject === "Generating..." ? (
        <GenericTableSkeleton onlyHead colCount={1} rowCount={1} />
      ) : (
        <div
          className="email-tiptap-subject-wrapper"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: "0.4rem",
            padding: "0.5rem 0.6rem",
          }}
        >
          <label
            htmlFor={`${TEMPLATE_ID}-subject`}
            style={{
              fontSize: "0.9rem",
              color: "#333",
              whiteSpace: "nowrap",
              lineHeight: "1.5rem",
            }}
          >
            Subject:
          </label>
          <input
            id={`${TEMPLATE_ID}-subject`}
            type="text"
            className="email-tiptap-subject"
            value={subject}
            onChange={handleSubjectChange}
            placeholder="Enter email subject..."
            disabled={!allowEditing}
          />
        </div>
      )}

      {!editor ? (
        <GenericTableSkeleton colCount={1} rowCount={1} />
      ) : (
        <div className={`email-tiptap-body ${allowEditing ? "" : "readonly"}`}>
          {allowEditing && (
            <div className="tiptap-toolbar">
              <button
                onClick={() => editor.chain().focus().toggleBold().run()}
                className={editor.isActive("bold") ? "active" : ""}
              >
                B
              </button>
              <button
                onClick={() => editor.chain().focus().toggleItalic().run()}
                className={editor.isActive("italic") ? "active" : ""}
              >
                I
              </button>
              <button
                onClick={() => editor.chain().focus().toggleStrike().run()}
                className={editor.isActive("strike") ? "active" : ""}
              >
                S
              </button>
              <button
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                className={editor.isActive("bulletList") ? "active" : ""}
              >
                • List
              </button>
              <button
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                className={editor.isActive("orderedList") ? "active" : ""}
              >
                1. List
              </button>
              <button
                onClick={() => {
                  const url = window.prompt("Enter link URL");
                  if (url) editor.chain().focus().setLink({ href: url }).run();
                }}
              >
                <FeatherIcon.Link style={{ height: "0.85rem" }} />
              </button>
            </div>
          )}

          <div className="editor-scroll-area">
            <EditorContent editor={editor} />
          </div>
        </div>
      )}
    </div>
  );
}
